import {
  readLocalePreference,
  saveLocalePreference,
} from '@/shared/i18n/locale-preference';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { I18nContext } from '@/app/providers/i18n-context';
import { translate, type TranslationKey } from '@/shared/i18n';
import {
  DEFAULT_LOCALE,
  type Locale,
  LOCALE_OPTIONS,
} from '@/shared/i18n/types';
function getInitialLocale(): Locale {
  try {
    return readLocalePreference(window.localStorage);
  } catch {
    return DEFAULT_LOCALE;
  }
}

/**
 * Lightweight i18n provider for UI localization.
 * Cross-cutting concern — intentionally kept in React Context, not Redux.
 */
export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(getInitialLocale);

  const setLocale = useCallback((nextLocale: Locale) => {
    setLocaleState(nextLocale);
    try {
      saveLocalePreference(nextLocale, window.localStorage);
    } catch {
      // The selected language remains available for this session.
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const t = useCallback(
    (key: TranslationKey, params?: Record<string, string>) =>
      translate(locale, key, params),
    [locale],
  );

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      t,
      localeOptions: LOCALE_OPTIONS,
    }),
    [locale, setLocale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
